import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Trash2, ArrowRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ProductImage } from "@/components/ProductImage";
import { PackagingHint } from "@/components/PackagingHint";
import { QuantityStepper } from "@/components/QuantityStepper";
import { MinimumOrderAlert } from "@/components/MinimumOrderAlert";
import { ShippingSummary } from "@/components/ShippingSummary";
import { useMinimumOrder } from "@/hooks/useMinimumOrder";
import { useShipping } from "@/hooks/useShipping";
import { EmptyState, PageHeading } from "@/components/states";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { expectedDeliveryDate, formatCurrency, formatDeliveryDate, getLocalizedField, isOrderable, isSupplierBackorder, isVatExempt, productImageUrl, unitLabel } from "@/lib/product";

const Mandje = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const {
    lines,
    totalExcl,
    setQuantity,
    removeLine,
    clear,
    refreshFromProducts,
    blockedLines,
    hasBlockedLines,
  } = useCart();
  const { producten, account } = useAuth();
  const navigate = useNavigate();
  const { minimum, ontbreekt, gehaald } = useMinimumOrder();
  const verzending = useShipping();

  // Verse voorraad/prijs/beschikbaarheid uit het CRM (briefing feb-2026 §2c).
  useEffect(() => {
    let actief = true;
    producten()
      .then((data) => { if (actief) refreshFromProducts(data); })
      .catch(() => { /* mandje blijft bruikbaar met de snapshot */ });
    return () => { actief = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (lines.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8" data-testid="cart-page">
        <PageHeading title={t("cart.title")} testId="cart-heading" />
        <div className="mt-6">
          <EmptyState
            title={t("cart.empty")}
            actionLabel={t("cart.emptyAction")}
            onAction={() => navigate("/producten")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="cart-page">
      <PageHeading
        title={t("cart.title")}
        subtitle={t("cart.subtitle")}
        testId="cart-heading"
        actions={
          <Button
            variant="outline"
            onClick={() => {
              clear();
              toast.success(t("cart.cleared"));
            }}
            data-testid="cart-clear-button"
          >
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("cart.clear")}
          </Button>
        }
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <MinimumOrderAlert minimum={minimum} ontbreekt={ontbreekt} gehaald={gehaald} testId="cart-minimum-alert" />
          {hasBlockedLines && (
            <Alert className="mb-4 border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10" data-testid="cart-unavailable-alert">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" aria-hidden="true" />
              <AlertDescription>
                {t("cart.unavailableWarning", { count: blockedLines.length })}
                <Button
                  variant="link"
                  className="ml-1 h-auto p-0 text-xs underline"
                  onClick={() => {
                    blockedLines.forEach((l) => removeLine(l.product_id));
                    toast.success(t("cart.unavailableRemoved"));
                  }}
                  data-testid="cart-remove-unavailable-button"
                >
                  {t("cart.removeUnavailable")}
                </Button>
              </AlertDescription>
            </Alert>
          )}
          <Card className="divide-y p-4 sm:p-6">
          {lines.map((line) => {
            const naam = getLocalizedField(line.product, "naam", lang);
            const orderable = isOrderable(line.product, account);
            const lineTotal =
              Number(line.product?.verkoopprijs || 0) * Number(line.aantal || 0);
            return (
              <div
                key={line.product_id}
                className={`flex flex-col gap-4 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center ${orderable ? "" : "opacity-70"}`}
                data-testid="cart-line-item"
                data-product-id={line.product_id}
                data-orderable={orderable ? "true" : "false"}
              >
                <Link to={`/producten/${line.product_id}`} className="shrink-0">
                  <ProductImage
                    src={productImageUrl(line.product?.hoofdfoto)}
                    alt={naam}
                    className="h-20 w-20 rounded-md"
                    imgClassName="p-2"
                  />
                </Link>

                <div className="min-w-0 flex-1">
                  <Link
                    to={`/producten/${line.product_id}`}
                    className="text-sm font-semibold leading-snug hover:text-primary"
                    data-testid="cart-line-item-title"
                  >
                    {naam}
                  </Link>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {line.product?.sku} · {formatCurrency(line.product?.verkoopprijs, lang)}{" "}
                    {t("price.exclVat")}
                    {line.product?.eenheid
                      ? ` · ${t("price.perUnit", { eenheid: unitLabel(line.product.eenheid, 1, lang) })}`
                      : ""}
                  </p>
                  {orderable && isSupplierBackorder(line.product) && (
                    <p className="mt-1 text-xs font-medium text-[hsl(var(--warning))]" data-testid="cart-line-item-supplier">
                      {t("stock.supplierShort", { datum: formatDeliveryDate(expectedDeliveryDate(), lang) })}
                    </p>
                  )}
                  {!orderable && (
                    <p
                      className="mt-1 text-xs font-medium text-[hsl(var(--warning))]"
                      data-testid="cart-line-item-unavailable"
                    >
                      {t("stock.unavailableNote")}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <div>
                    <Label
                      htmlFor={`cart-qty-${line.product_id}`}
                      className="text-xs text-muted-foreground"
                    >
                      {t("cart.quantity")}
                    </Label>
                    <QuantityStepper
                      product={line.product}
                      value={String(line.aantal)}
                      onChange={(waarde) => setQuantity(line.product_id, waarde)}
                      inputId={`cart-qty-${line.product_id}`}
                      testIdPrefix="cart-line-item-quantity"
                    />
                    <PackagingHint product={line.product} testId="cart-line-item-packaging-hint" />
                  </div>
                  <div className="min-w-[92px] text-right">
                    <p className="text-xs text-muted-foreground">{t("product.lineTotal")}</p>
                    <p
                      className="text-sm font-semibold tabular-nums"
                      data-testid="cart-line-item-total"
                    >
                      {formatCurrency(lineTotal, lang)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("cart.remove")}
                    onClick={() => {
                      removeLine(line.product_id);
                      toast.success(t("cart.removed"));
                    }}
                    data-testid="cart-line-item-remove-button"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            );
          })}
          </Card>
        </div>

        <Card className="h-fit p-6 lg:col-span-4">
          <h2 className="font-display text-lg font-semibold">{t("checkout.summary")}</h2>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("cart.lines", { count: lines.length })}</span>
            </div>
            <Separator className="my-3" />
            <div className="flex items-baseline justify-between">
              <span className="font-medium">{t("cart.totalExcl")}</span>
              <span className="text-lg font-semibold tabular-nums" data-testid="cart-total">
                {formatCurrency(totalExcl, lang)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="cart-vat-notice">
              {isVatExempt(account) ? t("cart.vatNoticeExempt") : t("cart.vatNotice")}
            </p>
            {verzending && <Separator className="my-3" />}
            <ShippingSummary verzending={verzending} testId="cart-shipping" toonToelichting={false} />
            {minimum && gehaald && (
              <p className="text-xs text-muted-foreground" data-testid="cart-minimum-ok">
                {t("cart.minimumOk", { minimum: formatCurrency(minimum, lang) })}
              </p>
            )}
          </div>

          <Button
            className="mt-6 w-full whitespace-normal"
            asChild={!hasBlockedLines && gehaald}
            disabled={hasBlockedLines || !gehaald}
            data-testid="cart-checkout-button"
          >
            {hasBlockedLines ? (
              <span>{t("cart.checkoutBlocked")}</span>
            ) : !gehaald ? (
              <span>{t("cart.minimumBlocked")}</span>
            ) : (
              <Link to="/bestellen">
                {t("cart.checkout")}
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </Button>
          <Button variant="ghost" className="mt-2 w-full" asChild data-testid="cart-continue-button">
            <Link to="/producten">{t("cart.continueShopping")}</Link>
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default Mandje;
